import React, { useState, useEffect, useCallback } from 'react';
import PublicAvailability from './PublicAvailability';
import PublicGameRoster from './PublicGameRoster';
import PublicCoachRoster from './PublicCoachRoster';
import PublicCourtRoster from './PublicCourtRoster';
import PublicMyRoster from './PublicMyRoster';
import PublicSignup from './PublicSignup';
import { publicFetch, setSessionToken } from '../utils/publicApi';
import './PublicPortal.css';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function PublicPortal({ token }) {
  const [personData, setPersonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invalidToken, setInvalidToken] = useState(false);
  const [activeTab, setActiveTab] = useState('myRoster');
  const [weekOffset, setWeekOffset] = useState(0);
  // When the user clicks "Add Participant" on a coach session header, we
  // jump to the Programs tab and pre-seed the form with the program of
  // that session. Cleared once the form is mounted (Programs tab opens
  // a fresh signup form once the seed has been applied).
  const [signupSeed, setSignupSeed] = useState(null);

  // SMS-OTP state. The portal is now strictly gated: until `mfaScreen` is
  // null the user sees only the verification UI, not the portal tabs.
  const [mfaScreen, setMfaScreen] = useState('verify'); // 'verify' | null
  const [hasMobile, setHasMobile] = useState(true);
  const [mobileMasked, setMobileMasked] = useState('');
  // Tab flags from the status endpoint, following the person's Active roles:
  // Game (referee / bench), Coach and Programs (coach), Court (court official).
  // Defaults to true so we
  // never hide a tab on a network failure.
  const [tabAvailability, setTabAvailability] = useState({ hasGames: true, hasCoaching: true, hasCourt: true, hasPrograms: true });
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaInfo, setMfaInfo] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Check session status on load. If the browser still has a valid signed
  // cookie OR a stored session token from a previous SMS verification,
  // skip straight to the portal.
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await publicFetch(`${API_BASE}/public/mfa/status/${token}`);
        if (res.status === 404) {
          setInvalidToken(true);
          setLoading(false);
          return;
        }
        if (res.ok) {
          const data = await res.json();
          setPersonData({ firstName: data.personName, fullName: data.personName });
          setHasMobile(!!data.hasMobile);
          setMobileMasked(data.mobileMasked || '');
          setMfaScreen(data.sessionValid ? null : 'verify');
          // Backend only returns these flags once sessionValid is true;
          // until then keep the defaults (all true) so we don't briefly
          // hide tabs while the user is on the verify screen.
          if (data.sessionValid) {
            setTabAvailability({
              hasGames: data.hasGames !== false,
              hasCoaching: data.hasCoaching !== false,
              hasCourt: data.hasCourt !== false,
              hasPrograms: data.hasPrograms !== false
            });
          }
        }
      } catch (err) {
        console.error('Status check failed:', err);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [token]);

  // Tick down the resend cooldown each second when active. Drives the
  // "Resend in Ns" disabled state on the resend button.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Request a fresh SMS code. Server enforces a 60s throttle — we mirror
  // that with the cooldown timer so the button stays disabled visually.
  const sendSmsCode = useCallback(async () => {
    setMfaError('');
    setMfaInfo('');
    setMfaLoading(true);
    try {
      const res = await fetch(`${API_BASE}/public/mfa/send-sms/${token}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSmsSent(true);
        setMobileMasked(data.mobileMasked || mobileMasked);
        setHasMobile(true);
        setMfaInfo(`Code sent to ${data.mobileMasked || 'your mobile'}.`);
        setResendCooldown(60);
      } else if (res.status === 429) {
        setSmsSent(true);
        setResendCooldown(data.retryAfterSeconds || 60);
        setMfaError(data.error || 'Please wait before requesting another code.');
      } else if (res.status === 400 && data?.error?.toLowerCase().includes('mobile')) {
        setHasMobile(false);
        setMfaError(data.error);
      } else {
        setMfaError(data.error || 'Could not send the verification SMS.');
      }
    } catch (err) {
      setMfaError('Connection error. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  }, [token, mobileMasked]);

  // Submit the entered code. On success, drop the verify screen and reveal
  // the portal. On failure we surface attempts-remaining; a "code missing"
  // response (expired or burnt by too many wrong tries) bounces the user
  // back to the "send code" state so they can request a fresh one.
  const verifyMfaCode = async (e) => {
    e.preventDefault();
    if (mfaCode.length !== 6) {
      setMfaError('Please enter the 6-digit code.');
      return;
    }
    setMfaLoading(true);
    setMfaError('');
    setMfaInfo('');
    try {
      const res = await fetch(`${API_BASE}/public/mfa/verify/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: mfaCode })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        // Persist the session token so cookie-blocked browsers can still
        // authenticate subsequent public/* requests via the ?_s= fallback.
        if (data.sessionToken) setSessionToken(token, data.sessionToken);
        setMfaScreen(null);
        setMfaCode('');
        setSmsSent(false);
      } else {
        setMfaError(data.error || 'Invalid code.');
        setMfaCode('');
        if (data.codeMissing) {
          setSmsSent(false);
          setResendCooldown(0);
        }
      }
    } catch (err) {
      setMfaError('Connection error. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const formatLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-AU', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  };

  const getDateRange = useCallback(() => {
    const today = new Date();
    const day = today.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday + (weekOffset * 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      startDate: formatLocalDate(monday),
      endDate: formatLocalDate(sunday)
    };
  }, [weekOffset]);

  const formatWeekLabel = () => {
    const { startDate, endDate } = getDateRange();
    return `${formatDate(startDate)} — ${formatDate(endDate)}`;
  };

  const showWeekNav = activeTab !== 'availability' && activeTab !== 'programs';

  if (loading) {
    return (
      <div className="public-portal">
        <div className="public-portal-card">
          <div className="public-portal-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (invalidToken) {
    return (
      <div className="public-portal">
        <div className="public-portal-card">
          <div className="public-portal-header">
            <h1>My Portal</h1>
          </div>
          <div className="public-portal-invalid">
            <h2>Invalid or Expired Link</h2>
            <p>This link is no longer valid. Please contact your administrator for a new link.</p>
          </div>
        </div>
      </div>
    );
  }

  // SMS verification screen — hard gate. Until the user enters the right
  // code (or the browser already has a valid 30-day cookie) they see only
  // this; the portal tabs below are not rendered.
  if (mfaScreen === 'verify') {
    return (
      <div className="public-portal">
        <div className="public-portal-card">
          <div className="public-portal-header">
            <h1>Verification Required</h1>
            <p className="public-portal-greeting">
              Welcome{personData ? `, ${personData.firstName}` : ''}! For your security, My Portal sends a one-time code by SMS to your registered mobile.
            </p>
          </div>

          <div className="mfa-setup-container">
            {!hasMobile ? (
              <div className="mfa-setup-intro">
                <p className="mfa-help-text">
                  We don't have a mobile number on file for you. Please contact your administrator so they can add a mobile to your record — once added, you'll be able to receive the SMS verification code.
                </p>
                {mfaError && <div className="mfa-error">{mfaError}</div>}
              </div>
            ) : !smsSent ? (
              <div className="mfa-setup-intro">
                <p className="mfa-instruction">
                  We'll send a 6-digit code to your mobile {mobileMasked ? `(${mobileMasked})` : ''}.
                  Enter the code on the next screen to unlock the portal for 30 days on this browser.
                </p>
                <button
                  className="mfa-btn-primary"
                  onClick={sendSmsCode}
                  disabled={mfaLoading}
                  style={{ marginTop: '1rem' }}
                >
                  {mfaLoading ? 'Sending...' : 'Send code by SMS'}
                </button>
                {mfaError && <div className="mfa-error">{mfaError}</div>}
                {mfaInfo && <div className="mfa-info">{mfaInfo}</div>}
              </div>
            ) : (
              <form onSubmit={verifyMfaCode} className="mfa-verify-form mfa-verify-centered">
                <label>Enter the 6-digit code we sent to {mobileMasked || 'your mobile'}:</label>
                <div className="mfa-code-input-row">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="mfa-code-input"
                    autoFocus
                    autoComplete="one-time-code"
                  />
                  <button type="submit" className="mfa-btn-primary" disabled={mfaLoading || mfaCode.length !== 6}>
                    {mfaLoading ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
                {mfaError && <div className="mfa-error">{mfaError}</div>}
                {mfaInfo && <div className="mfa-info">{mfaInfo}</div>}
                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={sendSmsCode}
                    disabled={mfaLoading || resendCooldown > 0}
                    className="mfa-btn-link"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: resendCooldown > 0 ? '#888' : '#1976d2',
                      textDecoration: resendCooldown > 0 ? 'none' : 'underline',
                      cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
                <p className="mfa-help-text" style={{ marginTop: '1rem' }}>
                  Codes expire 10 minutes after they're sent. If you've lost access to your mobile, contact your administrator.
                </p>
              </form>
            )}
          </div>

          <div className="public-portal-footer">
            <p>SwishIQ</p>
          </div>
        </div>
      </div>
    );
  }

  // Normal portal (SMS verified, signed cookie active).
  //
  // Game / Coach / Court / Programs show only for the roles the person is
  // Active for (per /public/mfa/status flags). Summary and Availability always show — they're either a roll-up
  // or an action-style page that's relevant to anyone.
  const tabs = [
    { key: 'myRoster', label: 'Summary' },
    ...(tabAvailability.hasGames    ? [{ key: 'games',    label: 'Game'  }] : []),
    ...(tabAvailability.hasCoaching ? [{ key: 'coaching', label: 'Coach' }] : []),
    ...(tabAvailability.hasCourt    ? [{ key: 'court',    label: 'Court' }] : []),
    { key: 'availability', label: 'Availability' },
    ...(tabAvailability.hasPrograms ? [{ key: 'programs', label: 'Programs' }] : [])
  ];

  // If the active tab got hidden by the availability flags (e.g. the user
  // had it bookmarked or it was the default), bounce them to Summary so
  // the content panel isn't blank.
  if (!tabs.some(t => t.key === activeTab)) {
    setTimeout(() => setActiveTab('myRoster'), 0);
  }

  return (
    <div className="public-portal">
      <div className="public-portal-card">
        <div className="public-portal-header">
          <h1>My Portal{personData ? ` - ${personData.firstName}` : ''}</h1>
        </div>

        {showWeekNav && (
          <div className="public-week-nav">
            <button onClick={() => setWeekOffset(prev => prev - 1)}>&larr; Prev</button>
            <span className="public-week-label">{formatWeekLabel()}</span>
            <button onClick={() => setWeekOffset(prev => prev + 1)}>Next &rarr;</button>
          </div>
        )}

        <div className="public-portal-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="public-portal-content">
          {activeTab === 'myRoster' && <PublicMyRoster token={token} weekOffset={weekOffset} />}
          {activeTab === 'games' && <PublicGameRoster token={token} weekOffset={weekOffset} />}
          {activeTab === 'coaching' && (
            <PublicCoachRoster
              token={token}
              weekOffset={weekOffset}
              onAddParticipant={(programTypeId) => {
                setSignupSeed({ programTypeId });
                setActiveTab('programs');
              }}
            />
          )}
          {activeTab === 'court' && <PublicCourtRoster token={token} weekOffset={weekOffset} />}
          {activeTab === 'availability' && <PublicAvailability token={token} embedded={true} />}
          {activeTab === 'programs' && (
            <PublicSignup
              token={token}
              initialProgramTypeId={signupSeed?.programTypeId}
              onSeedConsumed={() => setSignupSeed(null)}
            />
          )}
        </div>

        <div className="public-portal-footer">
          <p>SwishIQ</p>
        </div>
      </div>
    </div>
  );
}
